import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  Download,
  Copy,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  Eye,
  EyeOff,
  FileJson,
  FileSpreadsheet,
  Image,
  Calendar,
  DollarSign,
  Zap,
  User,
  Hash,
  ChevronDown,
  ChevronRight,
  Maximize2,
  Minimize2,
  File,
  Trash2,
} from "lucide-react";
import "./GFSWorkOrderDetail.css";

const FORM_TYPES_CONFIG = {
  aof: { name: "Account Opening Form (AOF)", color: "#3b82f6", bgColor: "#dbeafe" },
  redemption: { name: "Redemption (RP)", color: "#ef4444", bgColor: "#fee2e2" },
  additional_purchase: { name: "Additional Purchase (IUTA/AI)", color: "#06b6d4", bgColor: "#cffafe" },
};

// Collapsible Section Component
const CollapsibleSection = ({ title, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="gfs-collapsible-section">
      <button className="gfs-section-header" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        <span>{title.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
      </button>
      {isOpen && <div className="gfs-section-content">{children}</div>}
    </div>
  );
};

// Recursive JSON Renderer for Raw JSON view
const JsonRenderer = ({ data, depth = 0 }) => {
  if (data === null || data === undefined) {
    return <span className="gfs-json-null">null</span>;
  }

  if (typeof data === "boolean") {
    return <span className="gfs-json-boolean">{data.toString()}</span>;
  }

  if (typeof data === "number") {
    return <span className="gfs-json-number">{data}</span>;
  }

  if (typeof data === "string") {
    return <span className="gfs-json-string">"{data}"</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) return <span className="gfs-json-empty">[]</span>;
    return (
      <div className="gfs-json-array">
        {data.map((item, index) => (
          <div key={index} className="gfs-json-array-item" style={{ marginLeft: depth * 16 }}>
            <span className="gfs-json-index">[{index}]</span>
            <JsonRenderer data={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  if (typeof data === "object") {
    const entries = Object.entries(data);
    if (entries.length === 0) return <span className="gfs-json-empty">{"{}"}</span>;
    return (
      <div className="gfs-json-object">
        {entries.map(([key, value]) => (
          <div key={key} className="gfs-json-property" style={{ marginLeft: depth * 16 }}>
            <span className="gfs-json-key">{key}:</span>
            <JsonRenderer data={value} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  return <span>{String(data)}</span>;
};

const GFSWorkOrderDetail = () => {
  const { workOrderId } = useParams();
  const navigate = useNavigate();
  
  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("extracted");
  const [copiedField, setCopiedField] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(true);
  const [pdfFullscreen, setPdfFullscreen] = useState(false);
  const [fileLoadError, setFileLoadError] = useState(false);

  const [fileUrl, setFileUrl] = useState(null);
  const [fileBlob, setFileBlob] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);

  useEffect(() => {
    fetchWorkOrder();
  }, [workOrderId]);

  // Fetch file blob for PDF viewer
  useEffect(() => {
    if (workOrder && workOrderId) {
      fetchFileBlob();
    }
  }, [workOrder, workOrderId]);

  const fetchWorkOrder = async () => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("token");
      
      const res = await fetch(`/v1/gfs/api/work-orders/${workOrderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) {
        if (res.status === 404) {
          setError("Work order not found");
        } else {
          const data = await res.json();
          setError(data.message || "Failed to load work order");
        }
        return;
      }
      
      const data = await res.json();
      setWorkOrder(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchFileBlob = async () => {
    try {
      setFileLoading(true);
      setFileLoadError(false);
      const token = localStorage.getItem("token");
      
      console.log("[GFS] Fetching file for work order:", workOrderId);
      
      const res = await fetch(`/v1/gfs/api/work-orders/${workOrderId}/file`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      console.log("[GFS] File response status:", res.status);
      
      if (res.ok) {
        const blob = await res.blob();
        console.log("[GFS] File blob size:", blob.size, "type:", blob.type);
        
        if (blob.size > 0) {
          const url = URL.createObjectURL(blob);
          setFileUrl(url);
          setFileBlob(blob);
        } else {
          console.error("[GFS] Empty file blob");
          setFileLoadError(true);
        }
      } else {
        const errorText = await res.text();
        console.error("[GFS] File fetch error:", res.status, errorText);
        setFileLoadError(true);
      }
    } catch (err) {
      console.error("[GFS] Failed to load file:", err);
      setFileLoadError(true);
    } finally {
      setFileLoading(false);
    }
  };

  const handleDownloadJson = () => {
    if (!workOrder?.extracted_data) return;
    const blob = new Blob([JSON.stringify(workOrder.extracted_data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${workOrder.filename?.replace(/\.[^.]+$/, "") || workOrderId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadCSV = () => {
    if (!workOrder?.extracted_data) return;

    const data = workOrder.extracted_data;
    const flatData = {};

    // Flatten nested objects
    const flatten = (obj, prefix = "") => {
      for (const [key, value] of Object.entries(obj)) {
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          flatten(value, newKey);
        } else {
          flatData[newKey] = value;
        }
      }
    };
    flatten(data);

    const headers = Object.keys(flatData);
    const values = headers.map((h) => {
      const val = flatData[h];
      const str = String(val === undefined || val === null ? "" : val).replace(/"/g, '""');
      return `"${str}"`;
    });
    const csv = [headers.join(","), values.join(",")].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${workOrder.filename?.replace(/\.[^.]+$/, "") || workOrderId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadFile = async () => {
    try {
      // Use already loaded blob if available
      if (fileBlob) {
        const url = URL.createObjectURL(fileBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = workOrder?.filename || "document";
        a.click();
        URL.revokeObjectURL(url);
        return;
      }
      
      const token = localStorage.getItem("token");
      const res = await fetch(`/v1/gfs/api/work-orders/${workOrderId}/file`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) throw new Error("Failed to download file");
      
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = workOrder?.filename || "document";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download file: " + err.message);
    }
  };

  const copyToClipboard = useCallback((text, fieldName) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    });
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed": return <CheckCircle size={20} className="status-icon completed" />;
      case "processing": return <RefreshCw size={20} className="status-icon processing spin" />;
      case "failed": return <AlertCircle size={20} className="status-icon failed" />;
      default: return <Clock size={20} className="status-icon pending" />;
    }
  };

  const getFormTypeConfig = (formType) => FORM_TYPES_CONFIG[formType] || { name: formType, color: "#6b7280", bgColor: "#f3f4f6" };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  const isPdf = workOrder?.filename?.toLowerCase().endsWith('.pdf');

  if (loading) {
    return (
      <div className="gfs-detail-loading">
        <RefreshCw size={32} className="spin" />
        <p>Loading work order...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gfs-detail-error">
        <AlertCircle size={48} />
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate("/gfs/history")}>
          <ArrowLeft size={18} /> Back to Work Orders
        </button>
      </div>
    );
  }

  if (!workOrder) return null;

  const formConfig = getFormTypeConfig(workOrder.form_type);

  return (
    <div className="gfs-work-order-detail">
      {/* Back Button */}
      <button className="gfs-back-btn" onClick={() => navigate("/gfs/history")}>
        <ArrowLeft size={18} /> Back to Work Orders
      </button>

      {/* Header */}
      <div className="gfs-detail-header" style={{ borderLeftColor: formConfig.color }}>
        <div className="gfs-detail-header-left">
          <div className="gfs-detail-title">
            <h1>
              <FileText size={24} />
              {workOrder.filename || "Work Order"}
            </h1>
            <span className="gfs-form-badge" style={{ backgroundColor: formConfig.bgColor, color: formConfig.color }}>
              {formConfig.name}
            </span>
          </div>
          <div className="gfs-detail-meta">
            <span className="gfs-meta-item">
              <Hash size={14} /> <strong>ID:</strong> {workOrder._id}
              <button
                className="gfs-copy-btn-inline"
                onClick={() => copyToClipboard(workOrder._id, "id")}
                title="Copy ID"
              >
                <Copy size={14} />
                {copiedField === "id" && <span className="copied-tooltip">Copied!</span>}
              </button>
            </span>
            {workOrder.uid && (
              <span className="gfs-meta-item">
                <User size={14} /> <strong>UID:</strong> {workOrder.uid}
              </span>
            )}
          </div>
        </div>

        <div className="gfs-detail-header-right">
          <div className={`gfs-status-badge status-${workOrder.status}`}>
            {getStatusIcon(workOrder.status)}
            {workOrder.status}
          </div>
        </div>
      </div>

      {/* Info Cards */}
      <div className="gfs-info-cards">
        <div className="gfs-info-card">
          <Calendar size={20} />
          <div>
            <span className="label">Created</span>
            <span className="value">{formatDate(workOrder.created_at)}</span>
          </div>
        </div>
        <div className="gfs-info-card">
          <Clock size={20} />
          <div>
            <span className="label">Processing Time</span>
            <span className="value">{workOrder.processing_time ? `${workOrder.processing_time.toFixed(2)}s` : "-"}</span>
          </div>
        </div>
        <div className="gfs-info-card">
          <FileText size={20} />
          <div>
            <span className="label">Pages</span>
            <span className="value">{workOrder.total_pages || "-"}</span>
          </div>
        </div>
        <div className="gfs-info-card">
          <Zap size={20} />
          <div>
            <span className="label">Tokens</span>
            <span className="value">{workOrder.tokens?.toLocaleString() || "-"}</span>
          </div>
        </div>
        <div className="gfs-info-card">
          <DollarSign size={20} />
          <div>
            <span className="label">Cost</span>
            <span className="value">${workOrder.cost?.toFixed(4) || "0.0000"}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="gfs-action-bar">
        <button 
          className={`gfs-action-btn toggle ${showPdfViewer ? 'active' : ''}`}
          onClick={() => setShowPdfViewer(!showPdfViewer)}
        >
          {showPdfViewer ? <EyeOff size={16} /> : <Eye size={16} />}
          <span>{showPdfViewer ? "Hide Doc" : "Show Doc"}</span>
        </button>
        
        <div className="gfs-action-divider"></div>
        
        <button className="gfs-action-btn primary" onClick={handleDownloadJson}>
          <FileJson size={16} />
          <span>JSON</span>
        </button>
        <button className="gfs-action-btn secondary" onClick={handleDownloadCSV}>
          <FileSpreadsheet size={16} />
          <span>CSV</span>
        </button>
        <button className="gfs-action-btn" onClick={handleDownloadFile}>
          <Download size={16} />
          <span>PDF</span>
        </button>
        
        <div className="gfs-action-divider"></div>
        
        <button className="gfs-action-btn refresh" onClick={fetchWorkOrder} title="Refresh">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Split View Container */}
      <div className={`gfs-split-container ${showPdfViewer ? 'with-pdf' : 'no-pdf'} ${pdfFullscreen ? 'pdf-fullscreen' : ''}`}>
        {/* Document Viewer Panel */}
        {showPdfViewer && (
          <div className="gfs-pdf-panel">
            <div className="gfs-pdf-header">
              <h3>
                {isPdf ? <FileText size={18} /> : <Image size={18} />}
                {workOrder.filename || 'Document'}
              </h3>
              <div className="gfs-pdf-controls">
                <span className="gfs-file-type-badge">
                  {workOrder.filename?.split('.').pop()?.toUpperCase() || 'FILE'}
                </span>
                <button 
                  className="gfs-pdf-control-btn"
                  onClick={() => setPdfFullscreen(!pdfFullscreen)}
                  title={pdfFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                >
                  {pdfFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
                <button 
                  className="gfs-pdf-control-btn"
                  onClick={handleDownloadFile}
                  title="Download"
                >
                  <Download size={16} />
                </button>
              </div>
            </div>
            <div className="gfs-pdf-viewer">
              {fileLoading ? (
                <div className="gfs-pdf-unavailable">
                  <RefreshCw size={48} className="spin" />
                  <p>Loading document...</p>
                </div>
              ) : fileUrl && !fileLoadError ? (
                isPdf ? (
                  <iframe
                    src={fileUrl}
                    title="PDF Viewer"
                    className="gfs-pdf-iframe"
                    onError={() => setFileLoadError(true)}
                  />
                ) : (
                  <div className="gfs-image-viewer-scroll">
                    <img
                      src={fileUrl}
                      alt={workOrder.filename || 'Document'}
                      className="gfs-document-image"
                      onError={() => setFileLoadError(true)}
                    />
                  </div>
                )
              ) : (
                <div className="gfs-pdf-unavailable">
                  <File size={48} />
                  <p>{fileLoadError ? "Unable to load document" : "Document not available"}</p>
                  <button className="gfs-download-link" onClick={handleDownloadFile}>
                    <Download size={16} /> Download File
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Data Panel */}
        <div className="gfs-data-panel">
          {/* Tabs */}
          <div className="gfs-tabs">
            <button
              className={`gfs-tab ${activeTab === "extracted" ? "active" : ""}`}
              onClick={() => setActiveTab("extracted")}
            >
              <FileSpreadsheet size={16} /> Table View
            </button>
            <button
              className={`gfs-tab ${activeTab === "raw" ? "active" : ""}`}
              onClick={() => setActiveTab("raw")}
            >
              <FileJson size={16} /> Raw JSON
            </button>
            <button
              className={`gfs-tab ${activeTab === "signatures" ? "active" : ""}`}
              onClick={() => setActiveTab("signatures")}
            >
              <User size={16} /> Signatures
            </button>
            <button
              className={`gfs-tab ${activeTab === "metadata" ? "active" : ""}`}
              onClick={() => setActiveTab("metadata")}
            >
              <Hash size={16} /> Metadata
            </button>
          </div>

          {/* Tab Content */}
          <div className="gfs-tab-content">
            {/* Table View - Extracted Data */}
            {activeTab === "extracted" && (
              <div className="gfs-extracted-data">
                {workOrder.extracted_data ? (
                  Object.entries(workOrder.extracted_data).map(([sectionKey, sectionData]) => (
                    <CollapsibleSection key={sectionKey} title={sectionKey}>
                      {typeof sectionData === "object" && sectionData !== null ? (
                        <div className="gfs-fields-table">
                          <table>
                            <thead>
                              <tr>
                                <th>Field</th>
                                <th>Value</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(sectionData).map(([fieldKey, fieldValue]) => (
                                <tr key={fieldKey}>
                                  <td className="field-name">{fieldKey.replace(/_/g, ' ')}</td>
                                  <td className="field-value">
                                    {typeof fieldValue === "object"
                                      ? JSON.stringify(fieldValue, null, 2)
                                      : String(fieldValue ?? "-")}
                                  </td>
                                  <td className="field-actions">
                                    <button
                                      className="gfs-copy-btn"
                                      onClick={() =>
                                        copyToClipboard(
                                          typeof fieldValue === "object"
                                            ? JSON.stringify(fieldValue)
                                            : String(fieldValue ?? ""),
                                          `${sectionKey}.${fieldKey}`
                                        )
                                      }
                                    >
                                      <Copy size={14} />
                                      {copiedField === `${sectionKey}.${fieldKey}` && (
                                        <span className="copied-tooltip">Copied!</span>
                                      )}
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="gfs-simple-value">{String(sectionData)}</div>
                      )}
                    </CollapsibleSection>
                  ))
                ) : (
                  <div className="gfs-no-data">
                    <AlertCircle size={48} />
                    <p>No extracted data available</p>
                  </div>
                )}
              </div>
            )}

            {/* Raw JSON */}
            {activeTab === "raw" && (
              <div className="gfs-raw-json">
                <div className="gfs-raw-json-header">
                  <button onClick={handleDownloadJson}>
                    <Download size={16} /> Download
                  </button>
                  <button
                    onClick={() =>
                      copyToClipboard(JSON.stringify(workOrder.extracted_data, null, 2), "raw")
                    }
                  >
                    <Copy size={16} /> Copy
                    {copiedField === "raw" && <span className="copied-tooltip">Copied!</span>}
                  </button>
                </div>
                <pre className="gfs-json-viewer">
                  {workOrder.extracted_data ? (
                    <JsonRenderer data={workOrder.extracted_data} />
                  ) : (
                    "No data available"
                  )}
                </pre>
              </div>
            )}

            {/* Signatures */}
            {activeTab === "signatures" && (
              <div className="gfs-signatures-section">
                <h3>Signature Detection</h3>
                {(() => {
                  const signatureImages = workOrder.extracted_data?.signature_images || {};
                  const signatureKeys = Object.keys(signatureImages).filter(key => {
                    const value = signatureImages[key];
                    return value && value.trim() !== '';
                  });
                  
                  if (signatureKeys.length === 0) {
                    return (
                      <div className="gfs-no-signatures">
                        <User size={48} />
                        <p>No signatures detected in this document</p>
                        <span className="gfs-signatures-hint">
                          Signatures are automatically extracted using AWS Textract when processing new documents.
                        </span>
                      </div>
                    );
                  }
                  
                  // Format signature label for display
                  const formatLabel = (key) => {
                    return key
                      .replace(/_/g, ' ')
                      .replace(/P(\d+)$/, '(Page $1)')
                      .replace(/(\d+)$/, ' $1');
                  };
                  
                  return (
                    <div className="gfs-signatures-grid">
                      {signatureKeys.map(key => {
                        const base64Image = signatureImages[key];
                        return (
                          <div key={key} className="gfs-signature-card">
                            <div className="gfs-signature-header">
                              <User size={18} />
                              <span>{formatLabel(key)}</span>
                            </div>
                            <div className="gfs-signature-image-container">
                              <img src={base64Image} alt={formatLabel(key)} className="gfs-signature-image" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Metadata */}
            {activeTab === "metadata" && (
              <div className="gfs-metadata-section">
                <h3>Work Order Metadata</h3>
                <table className="gfs-metadata-table">
                  <tbody>
                    <tr><td>Work Order ID</td><td>{workOrder._id}</td></tr>
                    <tr><td>Filename</td><td>{workOrder.filename}</td></tr>
                    <tr><td>File Size</td><td>{workOrder.file_size ? `${(workOrder.file_size / 1024 / 1024).toFixed(2)} MB` : "-"}</td></tr>
                    <tr><td>Form Type</td><td>{formConfig.name}</td></tr>
                    <tr><td>Status</td><td>{workOrder.status}</td></tr>
                    <tr><td>UID</td><td>{workOrder.uid || "-"}</td></tr>
                    <tr><td>Total Pages</td><td>{workOrder.total_pages || "-"}</td></tr>
                    <tr><td>Processing Time</td><td>{workOrder.processing_time ? `${workOrder.processing_time.toFixed(2)} seconds` : "-"}</td></tr>
                    <tr><td>Input Tokens</td><td>{workOrder.input_tokens?.toLocaleString() || "-"}</td></tr>
                    <tr><td>Output Tokens</td><td>{workOrder.output_tokens?.toLocaleString() || "-"}</td></tr>
                    <tr><td>Total Tokens</td><td>{workOrder.tokens?.toLocaleString() || "-"}</td></tr>
                    <tr><td>Cost</td><td>${workOrder.cost?.toFixed(6) || "0.000000"}</td></tr>
                    <tr><td>Created At</td><td>{formatDate(workOrder.created_at)}</td></tr>
                    <tr><td>Updated At</td><td>{formatDate(workOrder.updated_at)}</td></tr>
                    {workOrder.api_log && (
                      <>
                        <tr className="gfs-metadata-subheader"><td colSpan={2}>API Request Details</td></tr>
                        <tr><td>Method</td><td>{workOrder.api_log.method || "-"}</td></tr>
                        <tr><td>Endpoint</td><td>{workOrder.api_log.endpoint || "-"}</td></tr>
                        <tr><td>IP</td><td>{workOrder.api_log.ip_address || "-"}</td></tr>
                        <tr>
                          <td>Status</td>
                          <td>
                            {workOrder.api_log.status || "-"}
                            {workOrder.api_log.status_code ? ` (${workOrder.api_log.status_code})` : ""}
                          </td>
                        </tr>
                        <tr>
                          <td>Request payload</td>
                          <td>
                            <pre className="gfs-metadata-payload">
                              {workOrder.api_log.request_params &&
                              Object.keys(workOrder.api_log.request_params).length > 0
                                ? JSON.stringify(workOrder.api_log.request_params, null, 2)
                                : "-"}
                            </pre>
                          </td>
                        </tr>
                        <tr>
                          <td>Response Time</td>
                          <td>
                            {workOrder.api_log.processing_time_seconds != null
                              ? `${parseFloat(workOrder.api_log.processing_time_seconds).toFixed(3)}s`
                              : "-"}
                          </td>
                        </tr>
                        <tr><td>Created By</td><td>{workOrder.api_log.created_by || "-"}</td></tr>
                        <tr><td>Timestamp</td><td>{formatDate(workOrder.api_log.timestamp)}</td></tr>
                      </>
                    )}
                    {workOrder.error && <tr className="error-row"><td>Error</td><td>{workOrder.error}</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GFSWorkOrderDetail;
